import ApolloClient from 'apollo-client'
import isEmpty from 'lodash/isEmpty'
import React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import { LOCIZE_NAMESPACE } from 'main/common'
import {
  initialParams,
  validate,
} from 'main/components/common/AuthModal/parent/InitialParentSignUpForm/Fields'
import {
  IParams,
  IValidation,
} from 'main/components/common/AuthModal/parent/InitialParentSignUpForm/types'
import Form from 'main/components/common/Form'
import { AppContext, IAppContext } from 'main/framework'
import {
  CREATE_PARENT_USER_MUTATION,
  CreateParentUser,
  CreateParentUserVariables,
} from 'main/gql/user'
import { i18nKeyPrefix } from 'main/pages/Search/i18n'
import { TextField } from 'main/ui'
import { login } from 'main/util/auth'
import { errorAlert, IPageAlert } from 'main/util/page'
import { UserSignUpSource } from 'main/util/user'
import { ApolloConsumer } from 'react-apollo'
import { useTranslation } from 'react-i18next'
import segment from 'util/analytics/segment'
import { Container, StyledButton, StyledTerms, TitleText } from './LoggedOutFrom.styles'

interface IProps {
  loggedOutTitle: string
  onComplete: () => void
  setAlert: (alert: IPageAlert | null) => void
}

const PRE = `${i18nKeyPrefix}-SAVE-LOGGED-OUT`

export default function LoggedOutForm(props: IProps) {
  const { loggedOutTitle, onComplete, setAlert } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const appContext = React.useContext(AppContext)

  const [params, setParams] = React.useState<IParams>(initialParams())
  const [validation, setValidation] = React.useState<IValidation>({})

  return (
    <ApolloConsumer>
      {client => (
        <div>
          <TitleText>{loggedOutTitle}</TitleText>
          <Mutation<CreateParentUser, CreateParentUserVariables>
            mutation={CREATE_PARENT_USER_MUTATION}
            onCompleted={data => handleCompleted(appContext, client, data, onComplete)}
            onError={() => setAlert(errorAlert)}
          >
            {(mutate, { loading }) => (
              <Form onSubmit={() => handleSubmit(params, setValidation, mutate)}>
                <Container>
                  <TextField
                    label={t(`${PRE}-NAME-LABEL`)}
                    placeholder={t(`${PRE}-NAME-PLACEHOLDER`)}
                    value={params.firstName}
                    onChange={firstName => handleChange({ firstName }, setParams)}
                    validationHint={validation.firstName}
                    required
                  />
                  <TextField
                    label={t(`${PRE}-EMAIL-LABEL`)}
                    placeholder={t(`${PRE}-EMAIL-PLACEHOLDER`)}
                    value={params.email}
                    onChange={email => handleChange({ email }, setParams)}
                    validationHint={validation.email}
                    required
                  />
                  <StyledButton kind={'regular'} disabled={loading} small={false} full>
                    {t(`${PRE}-SUBMIT`)}
                  </StyledButton>
                </Container>
              </Form>
            )}
          </Mutation>
          <StyledTerms />
        </div>
      )}
    </ApolloConsumer>
  )
}

function handleSubmit(
  params: IParams,
  setValidation: (validation: IValidation) => void,
  mutate: MutationFunction<CreateParentUser, CreateParentUserVariables>,
) {
  const validation = validate(params)

  setValidation(validation)
  if (!isEmpty(validation)) {
    return
  }

  const { email, firstName } = params

  mutate({
    variables: {
      user: {
        email: email.trim(),
        family: {},
        firstName,
        parentTosAgreed: true,
        signUpSource: UserSignUpSource.ParentSaveSearchInlineForm,
      },
    },
  })
}

function handleCompleted(
  context: IAppContext,
  client: ApolloClient<{}>,
  data: CreateParentUser,
  onComplete: () => void,
) {
  segment.track('New Parent Lead', {})
  segment.track('Save Search Inline Form Completed Sign Up', {})

  const credentials = data.createParentUser && data.createParentUser.credentials
  if (!credentials) {
    throw new Error('no credentials')
  }

  login(client, credentials, context).then(() => onComplete())
}

function handleChange(
  changes: Partial<IParams>,
  setParams: React.Dispatch<React.SetStateAction<IParams>>,
) {
  setParams(params => ({ ...params, ...changes }))
}
